import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, defer, timer } from 'rxjs';
import { filter, finalize, map, timeout } from 'rxjs/operators';
import { BotOrder, BotOrders, Order, OrdersWithPayment, } from '../_models/order';
import { environment } from '../../environments/environment';
import { Store } from '../_models/store';
import { IOrder, IOrderItem } from '../_interfaces/order';
import { TransationHistory } from '../_models/order-transaction-history';
import Utils from 'src/app/utils';
import { MatSnackBar } from '@angular/material/snack-bar';
import { int } from '@zxing/library/esm/customTypings';

@Injectable()
export class OrderService {
  endpoint = environment.apiUrl;
  orderEndpoint = `${this.endpoint}/backend/order/`;
  newOrderSubject: BehaviorSubject<IOrder> = new BehaviorSubject<IOrder>(null);
  editOrderSubject: Subject<Order> = new Subject<Order>();
  editSubmitSubject: Subject<IOrderItem> = new Subject<IOrderItem>();
  orderDoneSubject: Subject<void> = new Subject<void>();
  /** ENG-5865: the hash of an order whose payment the backend refused because the order was
   *  cancelled. Every payment screen open for that order closes itself. */
  orderCancelledSubject: Subject<string> = new Subject<string>();
  /** ENG-5867: orders this POS cancelled itself; their MQTT echo is not a cancel from elsewhere. */
  private cancelledHere = new Set<string>();
  openOrderCompleteSubject = new BehaviorSubject<any>({});
  openOrderComplete = this.openOrderCompleteSubject.asObservable();

  editMiscSubject: Subject<any> = new Subject<any>();

  /**
   * Fired when the order summary's read-only special-request row is tapped, so the POS menu
   * opens its Special Request dialog. The order-level note is edited in that dialog (ENG-4916
   * parity) while the summary only displays it.
   */
  openSpecialRequestSubject: Subject<void> = new Subject<void>();

  /**
   * Fired when the summary row's delete control is tapped, so the POS menu clears the
   * order-level note through the same persistence path the dialog saves through.
   */
  clearSpecialRequestSubject: Subject<void> = new Subject<void>();

  changeOrderSubject: Subject<BotOrder> = new Subject<BotOrder>();
  disableInputButton: boolean;
  activeTerminalList: any;
  public occasionUpdate: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public loadClosedOpenSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isCashCLose: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public keyboardValueSet: BehaviorSubject<any> = new BehaviorSubject<any>("");
  openPosLoginFromOpenOrder: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public removeItemUpdateMenuTab: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public tableUpdate: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public updateDefaultOccasion: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public closePreviousOverlays: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public resetStep: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  editDeliveryAddressSubject: Subject<OrdersWithPayment> = new Subject<OrdersWithPayment>();
  miscModifierAddedSubject: Subject<any> = new Subject<any>();
  selectedOrderItemIdSubject: BehaviorSubject<number | null> = new BehaviorSubject<number | null>(null);

  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };
  constructor(private http: HttpClient, private _snackBar: MatSnackBar) { }

  errorMessage(message: string, action: string, duration: number) {
    this._snackBar.open(message, action, {
      duration,
    });
  }

  /** The server's own words first ({message} / {error_message} / {error}); Angular's
   *  "Http failure response for …" boilerplate is never shown to a cashier. */
  /**
   * ENG-5960: the order on screen was deleted server-side. Training Mode OFF deletes every
   * training order (La Margarita 2026-10-29: the till kept its hash and toasted "Failed to fetch
   * order" on every tap for five minutes); the stale-draft sweep cancels abandoned tickets. The
   * backend now answers 404 {detail, error: 'order_not_found'} instead of a 500. The error
   * interceptor hands subscribers the body; a raw HttpErrorResponse is read too.
   */
  isOrderGone(err: any): boolean {
    const body = err?.error && typeof err.error === 'object' ? err.error : err;
    return err?.status === 404 || body?.error === 'order_not_found';
  }

  private vanishedOrderHash: string | null = null;

  /**
   * Drop a vanished order and start a fresh ticket, once: the ticket and the summary both
   * refresh the same hash, and the first one through does the work. orderDoneSubject is the
   * shell's "this order is over" signal (pay later / delete / checkout), so the POS, the summary
   * and the Orders shell all reset exactly as they do after a checkout.
   */
  recoverFromVanishedOrder(hash: string | null, message: string): void {
    if (!hash || this.vanishedOrderHash === hash) { return; }
    this.vanishedOrderHash = hash;
    if (localStorage.getItem('order_hash') === hash) { localStorage.removeItem('order_hash'); }
    this.errorMessage(message, 'OK', 4000);
    this.orderDoneSubject.next();
  }

  extractErrorMessage(error: any, fallback: string) {
    const body = error?.error;
    const served = body?.message || body?.error_message || body?.detail || (typeof body?.error === 'string' ? body.error : null);
    if (served) { return served; }
    const own = typeof error?.message === 'string' ? error.message : '';
    return own && !/^Http failure/i.test(own) ? own : fallback;
  }

  /**
   * ENG-5865: a cancelled order takes no more money. The backend answers a payment on one with
   * 409 {error: 'order_cancelled', detail: 'This order was cancelled at 9:22 PM (Replaced by the
   * terminal's next ticket). No payment was taken.'}. Pre-prod #2610-1549 was cancelled by the
   * stale-draft sweep while a POS still had it on the payment screen, then paid with Gift, and
   * stayed CANCELLED with $22.85 that was never given back.
   *
   * The message for that refusal, or null for any other error. The error interceptor hands
   * subscribers the response body; an HttpErrorResponse is read too.
   */
  orderCancelledText(err: any): string | null {
    const body = err?.error && typeof err.error === 'object' ? err.error : err;
    if (body?.error !== 'order_cancelled') { return null; }
    return body.detail || body.message || 'This order was cancelled. No payment was taken.';
  }

  /** True when [err] is the order_cancelled refusal: the cashier is told why, every payment
   *  screen of [orderHash] closes and the cancelled ticket leaves the POS. */
  handleOrderCancelled(err: any, orderHash: string): boolean {
    const text = this.orderCancelledText(err);
    if (!text) { return false; }
    this._snackBar.open(text, 'OK', { duration: 10000, verticalPosition: 'top' });
    this.isCashCLose.next(true);
    this.orderCancelledSubject.next(orderHash);
    if (localStorage.getItem('order_hash') === orderHash) {
      localStorage.removeItem('order_hash');
    }
    this.orderDoneSubject.next();
    return true;
  }

  updateTerminalStatus(terminalId, userId, sessionId, status) {
    const body = {
      terminal: terminalId,
      user: userId,
      status,
    };
    return this.http.patch<any>(
      `${this.endpoint}/backend/v2/terminal/selection/${sessionId}/`,
      JSON.stringify(body),
      this.httpOptions
    );
  }

  /** Station sign-in. ENG-5772: the ticket on screen rides along so the server can release
   *  the seated draft this station walked away from (a reload / relaunch) without touching it. */
  postLoginUser(body: any) {
    body = { ...(body || {}), order_hash: localStorage.getItem('order_hash') || null };
    return this.http.post<any>(
      `${this.endpoint}/backend/v2/terminal/selection/`,
      JSON.stringify(body),
      this.httpOptions
    );
  }

  getOrder(orderId: string): Observable<OrdersWithPayment> {
    return this.http.get<OrdersWithPayment>(this.orderEndpoint + orderId + '/');
  }

  printOrder(orderId: string, selectedTerminalName: string, receiptType: number) {
    const terminal_name = selectedTerminalName.toString();
    return this.http.get<string>(
      `${this.orderEndpoint}${orderId}/print/?terminal_name=${terminal_name}&receipt_type=${receiptType}`
    );
  }

  /**
   * Seat numbers: guest checks per seat. `seatParam` is 'all' (one check per seat),
   * '3' / '2,5' (those seats) or null for the whole-table check (receipt_type 3 as before).
   */
  printGuestChecks(orderId: string, seatParam: string | null, selectedTerminalName: string) {
    let params = new HttpParams()
      .set('terminal_name', (selectedTerminalName || 'NA').toString())
      .set('receipt_type', '3');
    if (seatParam) {
      params = params.set('seat', seatParam);
    }
    return this.http.get<string>(`${this.orderEndpoint}${orderId}/print/`, { params });
  }

  printOrderSplit(orderId: string, selectedTerminalName, splitId: string) {
    let terminal_name = selectedTerminalName.toString();
    return this.http.get<string>(this.orderEndpoint + orderId + "/split/" + splitId + "/print/?terminal_name=" + terminal_name)
  }

  orderReady(orderId: string, prepTime?: number) {
    let url = this.orderEndpoint + orderId + '/order_ready/';
    if (prepTime) {
      url += '?prep_time=' + prepTime;
    }
    return this.http.get<string>(url);
  }

  closeTableside(orderId: string) {
    return this.http.get<string>(
      this.orderEndpoint + orderId + '/close_tableside/'
    );
  }

  submitTableside(orderId: string) {
    return this.http.get<string>(
      this.orderEndpoint + orderId + '/submit_tableside_checkout/?opt_in=true'
    );
  }

  deleteMiscItem(miscId: string) {
    return this.http.delete<string>(
      `${this.endpoint}/backend/v2/orders/misc_items/${miscId}/`
    );
  }

  getStoreLogo(storeId: string) {
    return this.http.get<any>(
      `${this.endpoint}/backend/v2/stores/logo/${storeId}/`
    );
  }

  getTempOrder(storeId: string) {
    return this.http.get<any>(
      `${this.endpoint}/backend/order/temp/${storeId}/`
    );
  }

  getTempOrderWithPhone(storeId: string, phoneNumber: string) {
    return this.http.post<any>(
      `${this.endpoint}/backend/order/temp/${storeId}/`,
      { phone_number: phoneNumber }
    );
  }

  /** ENG-5772: `terminalId` is the station the order is being built on (the same
   *  localStorage `terminalId` that update_default_occasion gets). add_temp_order
   *  seeds the new order from THAT station's Default Load (occasion + ASAP/LATER);
   *  without it the backend used the store's first row, whatever station asked. */
  getTempPOSOrderWithPhone(
    storeId: string,
    phoneNumber: string,
    cashier: string,
    terminalId?: string | number | null,
    /** ENG-5772: the ticket this till is walking away from. The server frees its table at once
     *  if it was a seated draft that was never placed; any other draft on the station is left
     *  alone until it has gone idle — another device signed in as the same station may still
     *  be building it. */
    replaceOrderHash?: string | null
  ) {
    const body: any = { phone_number: phoneNumber, pos: true, cashier };
    if (terminalId !== undefined && terminalId !== null && String(terminalId).trim() !== '') {
      body.terminal_id = terminalId;
    }
    if (replaceOrderHash) {
      body.replace_order_hash = replaceOrderHash;
    }
    return this.http.post<any>(
      `${this.endpoint}/backend/order/temp/${storeId}/`,
      body
    );
  }

  getOrderByHash(hash: string) {
    return this.http.get<OrdersWithPayment>(
      `${this.endpoint}/backend/order/${hash}/`
    );
  }

  updateOccasionSchedule(hash: string, payload) {
    // ENG-5772: the till that seats a ticket owns it (the server records the station).
    const terminalId = localStorage.getItem('terminalId');
    if (terminalId && payload && typeof payload === 'object' && payload.terminal_id === undefined) {
      payload = { ...payload, terminal_id: terminalId };
    }
    return this.http.post<any>(
      `${this.endpoint}/backend/order/${hash}/update_occasion_schedule/`,
      payload
    );
  }

  updateDescription(id, payload) {
    return this.http.post<any>(
      `${this.endpoint}/backend/order/${id}/update_edit_description/`,
      payload
    );
  }

  updateInstruction(hash: string, payload) {
    return this.http.post<any>(
      `${this.endpoint}/backend/order/${hash}/update_instruction/`,
      payload
    );
  }

  /**
   * Item adds still in flight, per order hash. Pay Later / Checkout fire the kitchen
   * ticket with the item ids the summary has on screen; an add that has not come back
   * yet is either missing from that list or only there under a temporary id, so it was
   * left off the ticket (Hook Pizza Milton #2610-0905). The kitchen fire waits on this.
   */
  private pendingItemAdds = new Map<string, number>();
  private itemAddsSettled$ = new Subject<string>();

  addItemId(hash: string, orderId: string, payload) {
    // defer: counted per subscription, so an observable that is never subscribed
    // never holds the kitchen fire up; finalize runs on success, error and unsubscribe.
    return defer(() => {
      this.pendingItemAdds.set(hash, (this.pendingItemAdds.get(hash) ?? 0) + 1);
      return this.http.post<any>(
        `${this.endpoint}/backend/order/${hash}/${orderId}/new-modifiers/`,
        JSON.stringify(payload)
      ).pipe(finalize(() => {
        const left = (this.pendingItemAdds.get(hash) ?? 1) - 1;
        if (left > 0) {
          this.pendingItemAdds.set(hash, left);
        } else {
          this.pendingItemAdds.delete(hash);
          this.itemAddsSettled$.next(hash);
        }
      }));
    });
  }

  /** Resolves once no item add is in flight for this order, or after timeoutMs. */
  waitForPendingItemAdds(hash: string, timeoutMs = 10000): Promise<void> {
    if (!this.pendingItemAdds.get(hash)) return Promise.resolve();
    return new Promise<void>((resolve) => {
      const sub = this.itemAddsSettled$.pipe(filter(h => h === hash)).subscribe(() => done());
      const timeoutSub = timer(timeoutMs).subscribe(() => done());
      function done() {
        sub.unsubscribe();
        timeoutSub.unsubscribe();
        resolve();
      }
    });
  }

  async getParserMenuItems(hash: string): Promise<any> {
    return await this.http.get<any>(
      `${this.endpoint}/backend/order/${hash}/parser_menu_items/`
    ).pipe(map((r: any) => {
      return r;
    }))
      .toPromise().then(ret => {
        return ret;
      });
  }

  async removeItemId(hash: string, parsed_menu_item_id: string, payload): Promise<any> {
    return await this.http.post<any>(
      `${this.endpoint}/backend/order/${hash}/${parsed_menu_item_id}/remove/`,
      JSON.stringify(payload)
    ).pipe(map((r: any) => {
      return r;
    }))
      .toPromise().then(ret => {
        return ret;
      });
  }

  updateItemId(hash: string, itemId, payload) {
    return this.http.post<any>(
      `${this.endpoint}/backend/order/${hash}/${itemId}/modifiers/`,
      JSON.stringify(payload)
    );
  }

  // ── Seat numbers ──

  /** Move lines to a seat (`seat`, 0 = Table) or share them (`seat_shares`). Returns the full order. */
  setItemSeats(hash: string, payload: { item_ids: number[]; misc_item_ids: number[]; seat?: number; seat_shares?: number[] }) {
    return this.http.post<any>(
      `${this.endpoint}/backend/order/${hash}/items/seat/`,
      payload,
      this.httpOptions
    );
  }

  /** Per-seat totals of an order. */
  getSeats(hash: string) {
    return this.http.get<any>(`${this.endpoint}/backend/order/${hash}/seats/`);
  }

  /** How many guests sit at the table (goes through update_occasion_schedule). */
  updateGuestCount(hash: string, payload: { guest_count: number; [key: string]: any }) {
    return this.updateOccasionSchedule(hash, payload);
  }

  /** Every payment taken on an order, with the seat each one settled. */
  listPayments(orderHash: string) {
    const params = new HttpParams().set('order_hash', orderHash);
    return this.http.get<any>(`${this.endpoint}/backend/v2/pos/payments/`, { params });
  }

  /** What an order still owes and its split checks (by member / by item), as move destinations. */
  paymentDestinations(orderHash: string) {
    const params = new HttpParams().set('order_hash', orderHash);
    return this.http.get<any>(`${this.endpoint}/backend/v2/pos/payments/destinations/`, { params });
  }

  /** Move a payment to another seat, check or order; the card is never charged again
   *  (`difference_action: 'ADJUST'` changes the same authorization to what the destination owes). */
  movePayment(historyId: number | string, payload: any) {
    return this.http.post<any>(
      `${this.endpoint}/backend/v2/pos/payments/${historyId}/move/`,
      payload,
      this.httpOptions
    );
  }

  /** Merge other open table orders into this one; returns the merged order (+ `merge`). */
  mergeOrders(hash: string, payload: { source_order_hashes: string[]; pin_user: string | null; note?: string }) {
    return this.http.post<any>(
      `${this.endpoint}/backend/order/${hash}/merge/`,
      payload,
      this.httpOptions
    );
  }

  /**
   * Seat one party across several tables: orders open on the other tables merge into this one,
   * the order's table becomes "A1 + A2" and the guest count is set. Returns the order (+ `join`).
   */
  joinTables(hash: string, payload: { tables: string[]; source_order_hashes?: string[]; guest_count?: number | null; pin_user?: string | null }) {
    return this.http.post<any>(
      `${this.endpoint}/backend/order/${hash}/join_tables/`,
      payload,
      this.httpOptions
    );
  }

  /** Split seats of this order into new orders; returns this order (+ `split.new_orders`). */
  splitOrder(hash: string, payload: { groups: Array<{ seats: number[]; table?: string | null }>; pin_user: string | null; note?: string }) {
    return this.http.post<any>(
      `${this.endpoint}/backend/order/${hash}/split_order/`,
      payload,
      this.httpOptions
    );
  }

  saveItemOrder(storeId: string, payload) {
    return this.http.post<any>(
      `${this.endpoint}/backend/v2/pos/${storeId}/authorize`,
      payload
    );
  }

  createExternalMarketplaceOrder(payload) {
    return this.http.post<any>(
      `${this.endpoint}/backend/v2/orders/external-marketplace/`,
      payload,
      this.httpOptions
    );
  }

  getThirdPartyMarketplaceLabels(storeId: string) {
    let params = new HttpParams();
    if (storeId) {
      params = params.set("store_id", storeId);
    }
    return this.http.get<any>(
      `${this.endpoint}/backend/v2/orders/third-party-labels/`,
      { params }
    );
  }


  getTerminalId(storeId: string) {
    return this.http.get<any>(
      `${this.endpoint}/backend/v2/pos/?store_id=${storeId}&is_kiosk=false&is_driver=false&is_pax=false`
    );
  }

  /**
   * Every terminal the store has, kiosk, driver and PAX devices included. `getTerminalId`
   * above is the station picker's list and leaves those out on purpose; this one is for
   * naming a terminal a record already points at, where leaving them out reads as "no station".
   */
  getAllTerminals(storeId: string) {
    return this.http.get<any>(
      `${this.endpoint}/backend/v2/pos/?store_id=${storeId}`
    );
  }

  posAuthorize(terminalId: string, payload) {
    return this.http.post<any>(
      `${this.endpoint}/backend/v2/pos/${terminalId}/authorize/`,
      payload
    );
  }

  posTransactionStatus(terminalId: string, payload) {
    return this.http.post<any>(
      `${this.endpoint}/backend/v2/pos/${terminalId}/status/`,
      payload
    );
  }

  /** `extra` (ENG-5974): `approver_pin` / `approval_note` / `guest_selected`, merged into the body. */
  updateTip(orderId: string, tip: number, component: string, extra: object = {}) {
    return this.http.post<any>(
      this.orderEndpoint + orderId + '/update_tip/',
      JSON.stringify({ tip_percent: tip, component: component, ...extra }),
      this.httpOptions
    );
  }

  updateCustomTip(orderId: string, tip: number, component: string, extra: object = {}) {
    return this.http.post<string>(
      this.orderEndpoint + orderId + '/update_tip/',
      JSON.stringify({ tip, component, ...extra }),
      this.httpOptions
    );
  }

  updateQuantity(
    orderId: string,
    parsed_menu_item_id: number,
    quantity: number
  ) {
    const options = { headers: { 'Content-Type': 'application/json' } };
    return this.http.post<string>(
      this.orderEndpoint +
      orderId +
      '/' +
      parsed_menu_item_id +
      '/update_quantity/',
      JSON.stringify({ quantity }),
      this.httpOptions
    );
  }

  updateServiceCharge(orderId: string, serviceCharge: number) {
    return this.http.post<string>(
      this.orderEndpoint + orderId + '/update_service_charge/',
      JSON.stringify({ service_charge: serviceCharge }),
      this.httpOptions
    );
  }

  openForEdit(orderHash: string) {
    return this.http.post<any>(
      this.orderEndpoint + orderHash + '/open_for_edit/',
      JSON.stringify({}),
      this.httpOptions
    );
  }

  deleteItem(orderId: string, parsed_menu_item_id: number) {
    return this.http.get<string>(
      this.orderEndpoint + orderId + '/' + parsed_menu_item_id + '/delete/',
      this.httpOptions
    );
  }

  updatePhoneNumber(
    orderHash: string,
    phoneNumber: string,
    order_name: string,
    isPos = false
  ) {
    return this.http.post<string>(
      this.orderEndpoint + orderHash + '/update_phone_number/',
      JSON.stringify({
        phone_number: phoneNumber,
        order_name,
        is_pos: isPos,
      }),
      this.httpOptions
    );
  }

  updateDeliveryFee(orderId, delivery_fee) {
    const payload = { delivery_fee };
    return this.http.post<any>(
      `${this.endpoint}/backend/v2/orders/${orderId}/update_delivery_fee/`,
      JSON.stringify(payload),
      this.httpOptions
    );
  }

  getServiceAreas(store_id) {
    return this.http.get<any>(
      `${this.endpoint}/backend/v2/service-area/${store_id}/`,
      this.httpOptions
    );
  }

  removePromoCode(orderId) {
    return this.http.get<any>(
      this.orderEndpoint + orderId + '/remove_promo_code/',
      this.httpOptions
    );
  }

  applyPromoCode(orderId, promoCode) {
    return this.http.post<any>(
      this.orderEndpoint + orderId + '/apply_promo_code/',
      JSON.stringify({ promo_code: promoCode }),
      this.httpOptions
    );
  }


  payLater(orderId, payload, is_future: boolean) {
    return this.http.post(this.orderEndpoint + orderId + `/pay_later/?is_future=${is_future}`,
      JSON.stringify(payload),
      this.httpOptions);
  }

  wasCancelledHere(orderHash: string): boolean {
    return this.cancelledHere.has(orderHash);
  }

  /**
   * `suppressGuestSms`: the Cancel dialog is about to issue a courtesy credit,
   * whose text carries the cancellation news, so the endpoint's own plain
   * notice is held back. Everything else about the cancel runs as before.
   */
  cancelOrder(orderId, message, split_id, suppressGuestSms = false) {
    this.cancelledHere.add(orderId);
    let params = new HttpParams()
      .set('cancel_reason', message)
      .set('split_id', split_id);
    if (suppressGuestSms) {
      params = params.set('suppress_guest_sms', 'true');
    }
    return this.http.get<any>(
      this.orderEndpoint + orderId + '/cancel/', { params });
  }

  /** ENG-5772: the ticket's Delete on an order that was never placed (no Pay Later /
   *  checkout / kitchen send / payment). The backend cancels it and frees its table:
   *  200 `{order_hash, discarded: true, table}`; 409 `{code: "not_a_draft"}` when the
   *  order was placed (it is then left alone); 404 for an unknown hash. */
  discardDraftOrder(orderHash: string) {
    this.cancelledHere.add(orderHash);
    return this.http.post<any>(
      this.orderEndpoint + orderHash + '/discard_draft/',
      {},
      this.httpOptions
    );
  }

  closeCashOrder(orderId, payload) {
    return this.http.post<any>(
      this.orderEndpoint + orderId + '/close_cash_order/',
      JSON.stringify(payload),
      this.httpOptions
    );
  }

  closeKeyedOrder(orderId, payload) {
    return this.http.post<any>(
      this.orderEndpoint + orderId + '/close_keyed_order/',
      JSON.stringify(payload),
      this.httpOptions
    );
  }

  addNewCardGateway(
    storeId: string,
    phoneNumber: string,
    token: string,
    paymentGateway: string,
    isOneTime: boolean,
    card: any = {},
    zipcode: any
  ) {
    return this.http.post(
      this.endpoint + '/backend/payment/gateway/new_card/',
      JSON.stringify({
        store_id: storeId,
        token,
        phone_number: phoneNumber,
        payment_gateway: paymentGateway,
        is_one_time: isOneTime,
        card,
        zipcode
      }),
      this.httpOptions
    );
  }

  updatePreferredPayment(orderId: string, payload: any) {
    return this.http.post<string>(
      this.orderEndpoint + orderId + '/updated_preferred_payment/',
      JSON.stringify(payload),
      this.httpOptions
    );
  }

  getLastPreferredPayment(
    orderId: string,
    phoneNumber: string,
    paymentGateway: string
  ) {
    return this.http.post<any>(
      this.orderEndpoint + orderId + '/get_last_preferred_payment/',
      JSON.stringify({
        phone_number: phoneNumber,
        payment_gateway: paymentGateway,
      }),
      this.httpOptions
    );
  }

  getCards(storeId: string, phoneNumber: string, paymentGateway: string) {
    return this.http.get<any>(
      `${this.endpoint
      }/backend/payment/?store_id=${storeId}&phone_number=${encodeURIComponent(
        phoneNumber
      )}&payment_gateway=${paymentGateway}`,
      this.httpOptions
    );
  }

  deleteCard(cardDetails) {
    return this.http.post<any>(
      this.endpoint + '/backend/payment/delete/',
      JSON.stringify(cardDetails),
      this.httpOptions
    );
  }

  updateUser(orderHash, payload) {
    return this.http.post(`${this.endpoint}/backend/v2/pos/receipts/${orderHash}/update_user/`, payload, this.httpOptions);
  }

  sendTextReceipt(orderHash, payload) {
    return this.http.post(`${this.endpoint}/backend/v2/pos/receipts/${orderHash}/send_text_receipt/`, payload, this.httpOptions);
  }

  sendPaymentLink(orderHash, payload) {
    return this.http.post(`${this.endpoint}/backend/v2/pos/receipts/${orderHash}/send_payment_link/`, payload, this.httpOptions);
  }

  printPaperReceipt(orderHash, payload) {
    return this.http.post(`${this.endpoint}/backend/v2/pos/receipts/${orderHash}/print_receipt/`, payload, this.httpOptions);
  }

  getSplits(orderHash, payload) {
    // 30s ceiling so a request that never resolves surfaces as an error the
    // component can react to, instead of leaving isLoading=true and freezing
    // the split screen indefinitely. The backend answers this in well under a
    // second, so anything near 30s is a fault, not slowness.
    return this.http.post<any>(`${this.endpoint}/backend/v2/pos/split/${orderHash}/get_splits/`, payload, this.httpOptions)
      .pipe(timeout(30000));
  }

  processSplit(orderHash, payload) {
    return this.http.post(`${this.endpoint}/backend/v2/pos/split/${orderHash}/process_split/`, payload, this.httpOptions);
  }

  getDue(orderHash) {
    return this.http.get(`${this.endpoint}/backend/v2/pos/split/${orderHash}/get_due/`, this.httpOptions);
  }

  closeSplitPayment(orderHash, payload) {
    return this.http.post(`${this.endpoint}/backend/v2/pos/split/${orderHash}/close_split_payment/`, payload, this.httpOptions);
  }

  voidTransaction(orderHash, payload) {
    return this.http.post(`${this.endpoint}/backend/v2/pos/split/${orderHash}/void_transaction/`, payload, this.httpOptions);
  }

  refundSplitTransaction(orderHash, payload) {
    return this.http.post(`${this.endpoint}/backend/v2/pos/split/${orderHash}/refund/`, payload, this.httpOptions);
  }

  updateSplit(orderHash, payload) {
    return this.http.post(`${this.endpoint}/backend/v2/pos/split/${orderHash}/update_split/`, payload, this.httpOptions);
  }

  resetSplits(orderHash) {
    return this.http.get(`${this.endpoint}/backend/v2/pos/split/${orderHash}/reset/`, this.httpOptions);
  }

  resetSingleSplit(orderHash, payload) {
    return this.http.post(`${this.endpoint}/backend/v2/pos/split/${orderHash}/reset_split/`, payload, this.httpOptions);
  }

  addTipSplitTransaction(orderHash, payload) {
    return this.http.post(`${this.endpoint}/backend/v2/pos/split/${orderHash}/add_tip/`, payload, this.httpOptions);
  }

  splitAuthorize(terminalId: string, payload, optIn) {
    return this.http.post<any>(
      `${this.endpoint}/backend/v2/pos/split/${terminalId}/authorize/?opt_in=${optIn}&is_open_tab=true`,
      payload
    );
  }

  filterDeliveryAddress(store: Store, data: any) {
    return this.http.get<any>(`${this.endpoint}/backend/${store}/address-list-by-phone/?phone_number=${data}`);
  }
  posCloseBatch(storeId) {
    return this.http.get(`${this.endpoint}/backend/v2/pos/batch/${storeId}/close_batch/`, this.httpOptions);
  }

  posClosedBatches(storeId) {
    return this.http.get(`${this.endpoint}/backend/v2/pos/batch/${storeId}/closed_batches/`, this.httpOptions);
  }

  closedBatchCustom(payload) {
    return this.http.post(`${this.endpoint}/backend/v2/custom-batch-time/`, payload);
  }

  customClosedBatchTime(store) {
    return this.http.get(`${this.endpoint}/backend/v2/custom-batch-time/${store}/`);
  }

  closedOrders(storeId: number, filter = '', startDate = null, endDate = null,
    sortField = 'id', sortOrder = 'asc', pageNumber = 0, pageSize = 10, cashierId = '',
    occasions = [], payment = [], source = [], status = [], server_id = []) {

    let params = new HttpParams()
      .set('store_id', storeId.toString())
      .set('filter', filter)
      .set('sort_field', sortField)
      .set('sort_dir', sortOrder)
      .set('page_number', pageNumber.toString())
      .set('page_size', pageSize.toString())
      .set('closed_tab', 'true')
      .set('cashier_id', cashierId || '')
      .set('occasion', occasions.join(','))
      .set('payment', payment.join(','))
      .set('source', source.join(','))
      .set('status', status.join(','))
      .set('server_id', server_id.join(','))


    if (startDate) {
      // params = params.set('start_date', startDate.toISOString().slice(0, 10))
      params = params.set('start_date', Utils.toISOLocal(startDate).slice(0, 10))
    }

    if (endDate) {
      // params = params.set('end_date', endDate.toISOString().slice(0, 10))
      params = params.set('end_date', Utils.toISOLocal(endDate).slice(0, 10))
    }

    return this.http.get<BotOrders>(`${environment.apiUrl}/backend/v2/business/orders/${storeId}/closed_orders/`, { params });
  }


  openOrders(storeId: number, filter = '', startDate = null, endDate = null,
    sortField = 'id', sortOrder = 'asc', pageNumber = 0, pageSize = 10, cashierId = '',
    occasions = [], payment = [], source = [], status = [], server_id = []) {

    let params = new HttpParams()
      .set('store_id', storeId.toString())
      .set('filter', filter)
      .set('sort_field', sortField)
      .set('sort_dir', sortOrder)
      .set('page_number', pageNumber.toString())
      .set('page_size', pageSize.toString())
      .set('open_tab', 'true')
      .set('cashier_id', cashierId || '')
      .set('occasion', occasions.join(','))
      .set('payment', payment.join(','))
      .set('source', source.join(','))
      .set('status', status.join(','))
      .set('server_id', server_id.join(','))

    if (startDate) {
      // params = params.set('start_date', startDate.toISOString().slice(0, 10))
      params = params.set('start_date', Utils.toISOLocal(startDate).slice(0, 10))
    }

    if (endDate) {
      // params = params.set('end_date', endDate.toISOString().slice(0, 10))
      params = params.set('end_date', Utils.toISOLocal(endDate).slice(0, 10))

    }

    return this.http.get<BotOrders>(`${environment.apiUrl}/backend/v2/business/orders/${storeId}/open_orders/`, { params });
  }

  futureOrders(storeId: number, filter = '', startDate = null, endDate = null,
    sortField = 'id', sortOrder = 'asc', pageNumber = 0, pageSize = 10, cashierId = '',
    occasions = [], payment = [], source = []) {

    let params = new HttpParams()
      .set('store_id', storeId.toString())
      .set('filter', filter)
      .set('sort_field', sortField)
      .set('sort_dir', sortOrder)
      .set('page_number', pageNumber.toString())
      .set('page_size', pageSize.toString())
      .set('open_tab', "true")
      .set('cashier_id', cashierId || '')
      .set('occasion', occasions.join(','))
      .set('payment', payment.join(','))
      .set('source', source.join(','));

    if (startDate) {
      // params = params.set('start_date', startDate.toISOString().slice(0, 10))
      params = params.set('start_date', Utils.toISOLocal(startDate).slice(0, 10))
    }

    if (endDate) {
      // params = params.set('end_date', endDate.toISOString().slice(0, 10))
      params = params.set('end_date', Utils.toISOLocal(endDate).slice(0, 10))

    }

    return this.http.get<BotOrders>(`${environment.apiUrl}/backend/v2/business/orders/${storeId}/future_orders/`, { params });
  }

  processPostPayment(storeId: number | string, payload: any): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}/backend/v2/business/orders/${storeId}/payment_process/`, payload);
  }

  history(orderId) {
    return this.http.get<TransationHistory[]>(`${environment.apiUrl}/backend/v2/business/orders/${orderId}/history/`);
  }

  itemRefund(storeId: number | string, payload: any): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}/backend/v2/business/orders/${storeId}/item_refund/`, payload);
  }

  itemRefundPreview(storeId: number | string, payload: any): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}/backend/v2/business/orders/${storeId}/item_refund_preview/`, payload);
  }

  itemRefundHistory(orderHash: string): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}/backend/v2/business/orders/${orderHash}/item_refund_history/`);
  }

  viewOrderConfirm(orderId: string, data: any) {
    return this.http.get<string>(
      this.orderEndpoint + orderId + `/order_view_ready/${data}/`
    );
  }

  numberVerifyForCard(payload, hash) {
    return this.http.post(`${environment.apiUrl}/backend/card-verify/${hash}/otp/`, payload);
  }

  verifyCardOtp(payload, hash) {
    return this.http.post(`${environment.apiUrl}/backend/card-verify/${hash}/otp/verify/`, payload);

  }

  payLaterReceiptPrint(o_hash, selection) {
    return this.http.get(`${environment.apiUrl}/backend/store/${o_hash}/pay_later/${selection}/manual_receipt`);
  }

  splitOrderDetails(o_hash: any) {
    return this.http.get(`${environment.apiUrl}/backend/order_split_list/${o_hash}/`);
  }
  splitTransactionDetails(o_hash: any, split_id) {
    http://localhost:8000/backend/split_order_detail/0c324a30-5ea3-11ee-a606-01b7a62964f9/2257/
    return this.http.get(`${environment.apiUrl}/backend/split_order_detail/${o_hash}/${split_id}/`);
  }
  printOrderSplitManual(orderId: string, selectedTerminalName, splitId: string, selectionVal, data) {
    let terminal_name = selectedTerminalName.toString();
    return this.http.get<string>(this.orderEndpoint + orderId + "/split/" + splitId + "/print/?terminal_name=" + terminal_name + "&selection=" + selectionVal + "&given_cash=" + data.result + "&total=" + data.cashDueTotal)
  }
  manualPrintReceipt(o_hash: string, selection: string, terminalName: string, posLoggedUser: string): Observable<any> {
    let queryParams = new HttpParams();

    queryParams = queryParams.set('terminal_name', terminalName.toString());
    queryParams = queryParams.set('server_id', posLoggedUser.toString());

    return this.http.get(`${environment.apiUrl}/backend/store/${o_hash}/${selection}/manual_print`, { params: queryParams });
  }
  posDeliveryReorder(store_id: string, payload) {
    return this.http.post<any>(`${environment.apiUrl}/backend/order/${store_id}/pos_reorder/`, payload);
  }
  addQuickDiscount(orderId, payload) {
    return this.http.post(`${environment.apiUrl}/backend/v2/orders/pos_add_discount/${orderId}/`, payload);
  }

  removeQuickDiscount(orderId) {
    return this.http.post(`${environment.apiUrl}/backend/v2/orders/${orderId}/pos_remove_discount/`, {});
  }

  getSplitTransactionsHistory(orderId, splitId, includeTip = false) {
    return this.http.get<any>(`${environment.apiUrl}/backend/v2/pos/split/${orderId}/history/?is_tip=${includeTip}&split_id=${splitId}`, this.httpOptions);
  }

  updateServerId(order_hash, payload) {
    return this.http.post(`${environment.apiUrl}/backend/order/${order_hash}/update_server_id/`, payload);
  }

  deliveryOrderList(storeid, page_number: any, page_size: any) {
    let params = new HttpParams()
      .set('page_number', page_number)
      .set('page_size', page_size);
    return this.http.get(`${environment.apiUrl}/backend/${storeid}/delivery_order_list/`, { params });
  }

  driverList(driverid) {
    return this.http.get(`${environment.apiUrl}/backend/${driverid}/drivers_list/`);
  }

  assignOrderToDriver(payload: any, storeid: any) {
    return this.http.post(`${environment.apiUrl}/backend/${storeid}/update_driver_id/`, payload);
  }

  updateOrderStatus(payload: any, storeid: any) {
    return this.http.post(`${environment.apiUrl}/backend/${storeid}/update_delivery_status/`, payload);

  }

  updateTableStatus(store_id: Number, table_name: any) {
    const payload = {
      table_number: table_name
    }
    return this.http.post<any>(
      `${this.endpoint}/backend/order/${store_id}/update_table_status/`,
      payload
    );
  }

  defaultOccasionUpdate(hash: string, terminal_id: any, store: any) {
    return this.http.post<any>(`${this.endpoint}/backend/order/${store}/${hash}/${terminal_id}/update_default_occasion/`, this.httpOptions);
  }

  updateTaxCheck(hash: string, val: boolean) {
    return this.http.post<any>(`${this.endpoint}/backend/order/${hash}/${val}/update_tax_exempt/`, this.httpOptions);
  }

  /**
   * Look a gift card up by number. Only `intent: 'sell'` may register a number
   * nobody has seen before (that is how a new card is sold); redeeming, reloading
   * and balance checks get a 404 instead of leaving a stray blank card behind.
   */
  getPhysicalGiftcard(cardNumber, storeId, intent: 'sell' | 'redeem' | 'balance' | 'lookup' = 'lookup') {
    const params = new HttpParams()
      .set('gift_card', cardNumber)
      .set('store_id', storeId)
      .set('intent', intent)
    return this.http.get<any>(
      this.endpoint + '/backend/get-physical-giftcard/', { params });
  }

  closeGiftCardOrder(orderId, payload) {
    return this.http.post<any>(
      this.orderEndpoint + orderId + '/physical-giftcards-redemptions/', JSON.stringify(payload), this.httpOptions
    );
  }

  deleteMiscModItem(miscModId: string) {
    return this.http.delete<string>(
      `${this.endpoint}/backend/v2/orders/misc_modifier/${miscModId}/`
    );
  }

  sendItemsToKitchen(
    orderHash: string,
    // order_name / phone_number: the customer fields as currently typed on the POS. The
    // backend persists them before it builds the kitchen ticket, so a name entered just
    // before Pay Later / Checkout lands on the first print and not only on the reprint.
    payload: { itemIds: number[]; miscItemIds: number[]; reprint?: boolean; order_name?: string; phone_number?: string },
    opts?: { terminalName?: string; posLoggedUser?: string }
  ) {
    const terminalName = opts?.terminalName || 'NA';
    const serverId = opts?.posLoggedUser || 'NA';

    // Build query params
    let params = new HttpParams()
      .set('terminal_name', terminalName)
      .set('server_id', serverId);

    // Body contains only order + items
    const body = {
      order_hash: orderHash,
      ...payload
    };

    return this.http.post(
      `${this.endpoint}/backend/send_to_kitchen/`,
      body,
      { ...this.httpOptions, params }
    );
  }
  
  CancelPaymentInstruction(orderId: number): Observable<any> {
    return this.http.post(`${this.endpoint}/backend/v2/orders/${orderId}/cancel_instruction/`, {});
  }

  getOrderCounts(storeId: number): Observable<{ open: number; future: number; delivery: number }> {
    return this.http.get<{ open: number; future: number; delivery: number }>(
      `${environment.apiUrl}/backend/v2/business/orders/${storeId}/order_counts/`
    );
  }

  bulkArchiveOrders(orderIds: number[]) {
    return this.http.post<any>(
      `${environment.apiUrl}/backend/v2/business/orders/bulk_archive/`,
      { order_ids: orderIds }
    );
  }

  bulkCloseOrders(orderIds: number[]) {
    return this.http.post<any>(
      `${environment.apiUrl}/backend/v2/business/orders/bulk_close/`,
      { order_ids: orderIds }
    );
  }

  // ── v2 Tab API methods ──

  createTab(orderHash: string, payload: {
    cashier_id?: string;
    terminal_id?: string;
    table_number?: string;
    customer_name?: string;
    customer_phone?: string;
    server_id?: string;
  }) {
    return this.http.post<any>(
      `${this.endpoint}/backend/v2/tabs/create/`,
      { order_hash: orderHash, ...payload }
    );
  }

  listTabs(storeId: number, tabStatus = 'OPEN', page = 0, pageSize = 20) {
    const params = new HttpParams()
      .set('store_id', storeId.toString())
      .set('status', tabStatus)
      .set('page', page.toString())
      .set('page_size', pageSize.toString());
    return this.http.get<any>(
      `${this.endpoint}/backend/v2/tabs/list/`,
      { params }
    );
  }

  resumeTab(orderHash: string) {
    return this.http.post<any>(
      `${this.endpoint}/backend/v2/tabs/resume/`,
      { order_hash: orderHash }
    );
  }

  checkoutTab(orderHash: string) {
    return this.http.post<any>(
      `${this.endpoint}/backend/v2/tabs/checkout/`,
      { order_hash: orderHash }
    );
  }

  /** `extra` (ENG-5974): `approver_pin` / `approval_note` once a manager approved the tip. */
  closeTabV2(orderHash: string, tip?: string, extra: object = {}) {
    return this.http.post<any>(
      `${this.endpoint}/backend/v2/tabs/close/`,
      { order_hash: orderHash, tip: tip || '0', ...extra }
    );
  }

  cancelTab(orderHash: string, reason?: string) {
    this.cancelledHere.add(orderHash);
    return this.http.post<any>(
      `${this.endpoint}/backend/v2/tabs/cancel/`,
      { order_hash: orderHash, reason: reason || '' }
    );
  }

  verifyCard(orderHash: string, terminalId: string) {
    return this.http.post<any>(
      `${this.endpoint}/backend/v2/tabs/verify-card/`,
      { order_hash: orderHash, terminal_id: terminalId }
    );
  }
}
